/**
 * Two solvers, for two different questions.
 *
 * `countSolutions` answers "is this board the only board?" — the question the
 * generator asks a few hundred times per puzzle. It is an exhaustive search over
 * the candidate blocks from `placements.ts`: choose a block for the least-choice
 * cell on the board, and recurse. It knows nothing about how a person solves and
 * is not meant to.
 *
 * `Deducer` answers "how would a person get there?" — the question difficulty and
 * hints are made of. It only ever draws conclusions that *follow*, one rung of a
 * ladder at a time:
 *
 *   1. A number with one shape left. Everything it covers is its shade, and
 *      everything around it is the other. This is where a 1 in the open, or a
 *      number wedged into a corner, is read straight off the board.
 *   2. A number with several shapes left that agree somewhere: a cell every
 *      remaining shape covers is that number's shade, and a cell every shape
 *      leaves on its border is the other shade — even though which shape it will
 *      be is still open.
 *   3. Every cell belongs to some number's region, so a cell that no light
 *      number can reach is shadow, and vice versa. This is the rung that reasons
 *      about the board rather than about one number.
 *   4. Trial: assume a shade for one cell, follow rungs 1–3, and if it breaks,
 *      the cell is the other shade.
 *
 * The ladder is what grading measures, and the rung a deduction sits on is what
 * a hint tells the player to do. Both are only as honest as the order above: a
 * rung is only reached when everything below it is exhausted.
 *
 * There was briefly a fifth rung: when neither shade for a cell breaks, the two
 * branches can still *agree*, and a square that comes out light either way is
 * light. It is sound, and on boards with big regions it looked like the rung
 * that would finish them. It was not — measured over sixty boards a tier it
 * rescued one, while costing a second sweep and a copy of both branch shadings
 * on every trial that did not break, which is most of them. The boards it was
 * meant to save turn out not to stall for want of one more inference; they stall
 * twenty squares in with nothing anywhere on the board to go on, and the
 * generator drops those instead. Worth knowing before adding it back.
 */

import {
  EMPTY_MASK,
  type Geometry,
  type Mask,
  geometry,
  maskCells,
  maskEmpty,
  maskEquals,
  maskHas,
  maskIntersect,
  maskOverlaps,
  maskUnion,
  maskWith,
  maskWithout,
} from './board';
import { type Placement, allPlacements } from './placements';
import {
  LIGHT,
  SHADOW,
  UNKNOWN,
  type Clue,
  type Difficulty,
  type Mark,
  type Puzzle,
  type Shade,
  other,
} from './types';

// ── Exhaustive search ───────────────────────────────────────────────────────

export interface Search {
  /** Solutions found, up to the limit asked for. */
  count: number;
  /** The first one, when one was asked for. */
  solution: Shade[] | null;
  /** False when the deadline cut the search short, so `count` is a floor. */
  complete: boolean;
}

/**
 * Every way to tile the board with the numbers' blocks, up to `limit`.
 *
 * Choosing per *cell* rather than per number is what keeps this fast. The cell
 * with the fewest blocks still able to cover it is the tightest constraint on
 * the board, every solution has to cover it somehow, and a cell no block can
 * cover ends the branch immediately — which is most of what the search does.
 *
 * A finished tiling needs no further checking. Blocks never overlap, no two of
 * the same shade ever touch, and a block contains no number but its own, so a
 * covered board is exactly one region per number of the right size and shade.
 */
export function countSolutions(
  puzzle: Puzzle,
  limit = 2,
  deadline = Number.POSITIVE_INFINITY,
  lists?: Placement[][],
): Search {
  const geo = geometry(puzzle.width, puzzle.height);
  const clues = puzzle.clues;
  const blocks = lists ?? allPlacements(geo, clues);

  const byCell: Placement[][] = Array.from({ length: geo.cells }, () => []);
  for (const list of blocks) {
    for (const placement of list) {
      for (const cell of placement.list) (byCell[cell] as Placement[]).push(placement);
    }
  }

  const shadeOf = clues.map((clue) => clue.shade);
  const chosen: Placement[] = [];
  let count = 0;
  let solution: Shade[] | null = null;
  let nodes = 0;
  let complete = true;

  const record = (): void => {
    if (solution) return;
    const shades = new Array<Shade>(geo.cells).fill(LIGHT);
    for (const placement of chosen) {
      const shade = (clues[placement.clue] as Clue).shade;
      for (const cell of placement.list) shades[cell] = shade;
    }
    solution = shades;
  };

  const descend = (covered: Mask, lightBorder: Mask, shadowBorder: Mask): void => {
    if (count >= limit || !complete) return;
    if (maskEquals(covered, geo.full)) {
      count += 1;
      record();
      return;
    }
    nodes += 1;
    if ((nodes & 0xff) === 0 && Date.now() > deadline) {
      complete = false;
      return;
    }

    // Counted before anything is collected, and each count stops as soon as it
    // can no longer beat the best so far: the search visits every open cell at
    // every node, and only the winner's blocks are ever needed as a list. A cell
    // nothing can cover still counts to zero, which ends the branch as before.
    let fewestCell = -1;
    let fewestCount = Number.POSITIVE_INFINITY;
    for (let cell = 0; cell < geo.cells; cell += 1) {
      if (maskHas(covered, cell)) continue;
      let count = 0;
      for (const placement of byCell[cell] as Placement[]) {
        if (maskOverlaps(placement.cells, covered)) continue;
        const border = shadeOf[placement.clue] === LIGHT ? lightBorder : shadowBorder;
        if (maskOverlaps(placement.cells, border)) continue;
        count += 1;
        if (count >= fewestCount) break;
      }
      // No block can cover this cell, so nothing below this node is a solution.
      if (count === 0) return;
      if (count < fewestCount) {
        fewestCount = count;
        fewestCell = cell;
        if (count === 1) break;
      }
    }
    if (fewestCell < 0) return;
    const fewest: Placement[] = [];
    for (const placement of byCell[fewestCell] as Placement[]) {
      if (maskOverlaps(placement.cells, covered)) continue;
      const border = shadeOf[placement.clue] === LIGHT ? lightBorder : shadowBorder;
      if (maskOverlaps(placement.cells, border)) continue;
      fewest.push(placement);
    }

    for (const placement of fewest) {
      const light = (clues[placement.clue] as Clue).shade === LIGHT;
      chosen.push(placement);
      descend(
        maskUnion(covered, placement.cells),
        light ? maskUnion(lightBorder, placement.border) : lightBorder,
        light ? shadowBorder : maskUnion(shadowBorder, placement.border),
      );
      chosen.pop();
      if (count >= limit || !complete) return;
    }
  };

  descend(EMPTY_MASK, EMPTY_MASK, EMPTY_MASK);
  return { count, solution, complete };
}

// ── The ladder ──────────────────────────────────────────────────────────────

export type Rung = 1 | 2 | 3 | 4;

export interface Assignment {
  cell: number;
  shade: Shade;
}

export interface Deduction {
  rung: Rung;
  /** The number the reasoning is about, when it is about one. */
  clue: number | null;
  /** The cell the reasoning is about, when it is about one. */
  cell: number | null;
  assignments: Assignment[];
  /** Rung 4 only: the shade that was supposed, and broke. */
  supposed?: Shade;
  /** Rung 4 only: the number that supposition left with no shape, if one did. */
  breaks?: number | null;
  /** Rung 4 only: otherwise, the square it left that no number could explain. */
  breaksAt?: number | null;
}

/**
 * What follows from what is on the board, one step at a time.
 *
 * The state is the shading the player can see plus, for each number, the blocks
 * still open to it. Everything a rung says is derived from those blocks, so
 * `refine` — which drops the blocks the shading has ruled out — runs before every
 * step and is the only place knowledge turns into progress.
 */
export class Deducer {
  readonly geo: Geometry;
  readonly clues: readonly Clue[];
  readonly shades: Mark[];
  /** True once something impossible has been derived. */
  broken = false;
  /**
   * The number whose last shape a contradiction took away, when that is what
   * broke — which is what a trial hint names as the thing to watch for.
   */
  brokenClue: number | null = null;
  /**
   * The square no number could explain any more, when that is what broke
   * instead: one nothing can reach, or one shaded that nothing of its shade can.
   */
  brokenCell: number | null = null;

  private readonly puzzle: Puzzle;
  /**
   * The blocks still open to each number.
   *
   * Never edited in place: `refine` swaps in a shorter list. That is what lets a
   * clone share every list with the state it came from until one of them learns
   * something, which is most of what a trial costs.
   */
  private lists: Placement[][];
  /** Cells known to be each shade, indexed by shade. */
  private known: Mask[];
  /** `known` as it stood when `refine` last ran, so it only tests what is new. */
  private refined: Mask[];
  /**
   * For each number, the length its block list had when rungs 1 and 2 last had
   * nothing to say about it, or -1.
   *
   * A list only ever shrinks, so one that is still that long is the same list,
   * and every square it could settle was already settled then. Skipping it is
   * what keeps `next` from re-intersecting every number's blocks on every step.
   */
  private quiet: Int32Array;

  constructor(puzzle: Puzzle, marks?: readonly Mark[], lists?: readonly Placement[][]) {
    this.puzzle = puzzle;
    this.geo = geometry(puzzle.width, puzzle.height);
    this.clues = puzzle.clues;
    this.lists = (lists ?? allPlacements(this.geo, puzzle.clues)).slice();
    this.shades = marks ? marks.slice() : new Array<Mark>(this.geo.cells).fill(UNKNOWN);
    // A number's own cell is its shade, given, whether or not the caller said so.
    for (const clue of puzzle.clues) this.shades[clue.cell] = clue.shade;
    this.known = [EMPTY_MASK, EMPTY_MASK, EMPTY_MASK];
    for (let cell = 0; cell < this.geo.cells; cell += 1) {
      const shade = this.shades[cell] as Mark;
      if (shade !== UNKNOWN) this.known[shade] = maskWith(this.known[shade] as Mask, cell);
    }
    this.refined = [EMPTY_MASK, EMPTY_MASK, EMPTY_MASK];
    this.quiet = new Int32Array(puzzle.clues.length).fill(-1);
  }

  /**
   * A separate state that starts where this one is — what a trial supposes on.
   *
   * The blocks are handed over rather than enumerated again: they are the
   * expensive part, and a trial that had to rebuild them would cost more than
   * the deduction is worth. So is what `refine` and `next` had already worked
   * out, since it is true of the copy for the same reasons it is true here.
   */
  clone(): Deducer {
    const copy = new Deducer(this.puzzle, this.shades, this.lists);
    copy.broken = this.broken;
    copy.brokenClue = this.brokenClue;
    copy.brokenCell = this.brokenCell;
    copy.refined = this.refined.slice();
    copy.quiet = this.quiet.slice();
    return copy;
  }

  assign(cell: number, shade: Shade): void {
    const current = this.shades[cell] as Mark;
    if (current === shade) return;
    if (current !== UNKNOWN) {
      this.broken = true;
      return;
    }
    this.shades[cell] = shade;
    this.known[shade] = maskWith(this.known[shade] as Mask, cell);
  }

  apply(deduction: Deduction): void {
    for (const assignment of deduction.assignments) this.assign(assignment.cell, assignment.shade);
  }

  solved(): boolean {
    return this.shades.every((shade) => shade !== UNKNOWN);
  }

  /** Drops the blocks the shading has ruled out. */
  refine(): void {
    if (this.broken) return;
    // Only what was learned since the last pass can rule anything new out: every
    // block still listed has already been tested against the rest.
    const fresh = [
      EMPTY_MASK,
      maskWithout(this.known[LIGHT] as Mask, this.refined[LIGHT] as Mask),
      maskWithout(this.known[SHADOW] as Mask, this.refined[SHADOW] as Mask),
    ];
    this.refined = this.known.slice();
    for (let index = 0; index < this.lists.length; index += 1) {
      const shade = (this.clues[index] as Clue).shade;
      const mine = fresh[shade] as Mask;
      const theirs = fresh[other(shade)] as Mask;
      if (maskEmpty(mine) && maskEmpty(theirs)) continue;
      const list = this.lists[index] as Placement[];
      const kept = list.filter(
        // A block may not cover a cell of the other shade, and its border may not
        // hold a cell of its own — that would be its region touching itself.
        (placement) =>
          !maskOverlaps(placement.cells, theirs) && !maskOverlaps(placement.border, mine),
      );
      if (kept.length === 0) {
        this.broken = true;
        this.brokenClue = index;
        return;
      }
      if (kept.length !== list.length) this.lists[index] = kept;
    }
  }

  /** The blocks still open to one number. */
  candidates(index: number): readonly Placement[] {
    return this.lists[index] as Placement[];
  }

  /**
   * The easiest thing that follows, or null when nothing on rungs 1–3 does.
   *
   * Rung order is the whole contract: a hint that offers rung 3 is promising the
   * player there is no rung 1 or 2 left to find, and a grade that says "needed
   * rung 3" is saying the same thing about the board.
   */
  next(): Deduction | null {
    this.refine();
    if (this.broken) return null;

    for (let index = 0; index < this.lists.length; index += 1) {
      const list = this.lists[index] as Placement[];
      if (list.length !== 1 || this.quiet[index] === 1) continue;
      const found = this.fromNumber(index);
      if (found) return found;
      this.quiet[index] = 1;
    }

    for (let index = 0; index < this.lists.length; index += 1) {
      const list = this.lists[index] as Placement[];
      if (list.length < 2 || this.quiet[index] === list.length) continue;
      const found = this.fromNumber(index);
      if (found) return found;
      this.quiet[index] = list.length;
    }

    const reach = this.reach();
    if (this.stranded(reach)) return null;
    for (let cell = 0; cell < this.geo.cells; cell += 1) {
      if (this.shades[cell] !== UNKNOWN) continue;
      const light = maskHas(reach[LIGHT] as Mask, cell);
      const shadow = maskHas(reach[SHADOW] as Mask, cell);
      if (!light && !shadow) {
        this.broken = true;
        this.brokenCell = cell;
        return null;
      }
      if (light === shadow) continue;
      return {
        rung: 3,
        clue: null,
        cell,
        assignments: [{ cell, shade: light ? LIGHT : SHADOW }],
      };
    }
    return null;
  }

  /**
   * Everything on the easiest rung that has anything, rather than only the first.
   *
   * What a person could see next, in other words, which is what `flowOf` needs:
   * it asks whether the next thing to see is somewhere near the last thing seen.
   * Empty when rungs 1–3 are exhausted, which is where a trial begins.
   */
  options(): Deduction[] {
    this.refine();
    if (this.broken) return [];
    for (const rung of [1, 2] as const) {
      const found: Deduction[] = [];
      for (let index = 0; index < this.lists.length; index += 1) {
        const list = this.lists[index] as Placement[];
        if ((rung === 1) !== (list.length === 1) || this.quiet[index] === list.length) continue;
        const deduction = this.fromNumber(index);
        if (deduction) found.push(deduction);
        else this.quiet[index] = list.length;
      }
      if (found.length > 0) return found;
    }
    const reach = this.reach();
    if (this.stranded(reach)) return [];
    const found: Deduction[] = [];
    for (let cell = 0; cell < this.geo.cells; cell += 1) {
      if (this.shades[cell] !== UNKNOWN) continue;
      const light = maskHas(reach[LIGHT] as Mask, cell);
      const shadow = maskHas(reach[SHADOW] as Mask, cell);
      if (!light && !shadow) {
        this.broken = true;
        this.brokenCell = cell;
        return [];
      }
      if (light === shadow) continue;
      found.push({
        rung: 3,
        clue: null,
        cell,
        assignments: [{ cell, shade: light ? LIGHT : SHADOW }],
      });
    }
    return found;
  }

  /**
   * Rung 4: assume, follow, and take the other shade when it breaks.
   *
   * Cells next to something already known come first. A trial is only as good as
   * the contradiction it runs into, and a cell in the middle of an undecided
   * patch has nothing to contradict — starting at the edge of what is known finds
   * the break in a handful of steps instead of exhausting the board.
   *
   * The deduction carries what was supposed and which number it left with
   * nowhere to go, when it was a number, so a hint can tell the player what to
   * watch for rather than only where to start.
   */
  trial(deadline = Number.POSITIVE_INFINITY, focus: readonly number[] = []): Deduction | null {
    this.refine();
    if (this.broken) return null;
    for (const cell of this.trialOrder(focus)) {
      if (Date.now() > deadline) return null;
      for (const shade of [LIGHT, SHADOW] as Shade[]) {
        const probe = this.clone();
        probe.assign(cell, shade);
        probe.saturate();
        if (!probe.broken) continue;
        return {
          rung: 4,
          clue: null,
          cell,
          assignments: [{ cell, shade: other(shade) }],
          supposed: shade,
          breaks: probe.brokenClue,
          breaksAt: probe.brokenClue === null ? probe.brokenCell : null,
        };
      }
    }
    return null;
  }

  /** Runs rungs 1–3 to a standstill. */
  run(): void {
    this.saturate();
  }

  /**
   * Rungs 1–3 to a standstill, as fast as they will go.
   *
   * Every rung only ever adds to what is known, and adding knowledge only ever
   * lets a rung conclude more, so where they stall — or whether they break — does
   * not depend on the order they are taken in. `next` takes them one at a time
   * because grading counts the steps and a hint names one; a trial wants only
   * the end state, so this takes everything each sweep finds before refining
   * again, which is most of the cost of a trial gone.
   */
  saturate(): void {
    for (;;) {
      this.refine();
      if (this.broken) return;
      let progressed = false;
      for (let index = 0; index < this.lists.length; index += 1) {
        const list = this.lists[index] as Placement[];
        if (this.quiet[index] === list.length) continue;
        const found = this.fromNumber(index);
        if (found) {
          this.apply(found);
          if (this.broken) return;
          progressed = true;
        } else {
          this.quiet[index] = list.length;
        }
      }
      if (progressed) continue;

      const reach = this.reach();
      if (this.stranded(reach)) return;
      for (let cell = 0; cell < this.geo.cells; cell += 1) {
        if (this.shades[cell] !== UNKNOWN) continue;
        const light = maskHas(reach[LIGHT] as Mask, cell);
        const shadow = maskHas(reach[SHADOW] as Mask, cell);
        if (!light && !shadow) {
          this.broken = true;
          this.brokenCell = cell;
          return;
        }
        if (light === shadow) continue;
        this.assign(cell, light ? LIGHT : SHADOW);
        progressed = true;
      }
      if (this.broken || !progressed) return;
    }
  }

  /**
   * Where to try first: beside what is known, and beside `focus` before that.
   *
   * `focus` is the squares the last step settled. Grading passes none, so its
   * trials — and the counts the tiers are cut from — are what they always were;
   * `flowOf` passes the last step, because a person stuck after a move looks
   * where they just were before they look anywhere else.
   */
  private trialOrder(focus: readonly number[] = []): number[] {
    const hot = new Set<number>();
    for (const cell of focus) {
      for (const neighbor of this.geo.neighbors[cell] as number[]) hot.add(neighbor);
    }
    const first: number[] = [];
    const touching: number[] = [];
    const rest: number[] = [];
    for (let cell = 0; cell < this.geo.cells; cell += 1) {
      if (this.shades[cell] !== UNKNOWN) continue;
      if (hot.has(cell)) {
        first.push(cell);
        continue;
      }
      const near = (this.geo.neighbors[cell] as number[]).some(
        (neighbor) => this.shades[neighbor] !== UNKNOWN,
      );
      (near ? touching : rest).push(cell);
    }
    return [...first, ...touching, ...rest];
  }

  /**
   * Whether a square already shaded is out of reach of every number its shade.
   *
   * Rung 3's rule read the other way round. Every square belongs to some
   * number's region, so a light square no light number can still grow over is
   * not a square anything can explain — the board is broken. Without this a
   * trial could shade a square, watch the numbers around it give up every shape
   * that covered it, and never notice; which of two trials broke then depended
   * on the order the rungs happened to be taken in.
   */
  private stranded(reach: readonly Mask[]): boolean {
    for (const shade of [LIGHT, SHADOW] as const) {
      const lost = maskWithout(this.known[shade] as Mask, reach[shade] as Mask);
      if (maskEmpty(lost)) continue;
      this.broken = true;
      this.brokenCell = maskCells(lost)[0] as number;
      return true;
    }
    return false;
  }

  /** The cells each shade's numbers can still reach, indexed by shade. */
  private reach(): Mask[] {
    const out = [EMPTY_MASK, EMPTY_MASK, EMPTY_MASK];
    for (let index = 0; index < this.lists.length; index += 1) {
      const shade = (this.clues[index] as Clue).shade;
      let union = out[shade] as Mask;
      for (const placement of this.lists[index] as Placement[]) {
        union = maskUnion(union, placement.cells);
      }
      out[shade] = union;
    }
    return out;
  }

  /**
   * What rungs 1 and 2 say about one number, or null if nothing new.
   *
   * One block left is rung 1: all of it is settled. Several is rung 2, and only
   * where they agree — a cell every block covers, or one every block leaves on
   * its border. A cell on every block's border but inside one of them is not
   * settled: whether it ends up in the region or beside it is still open.
   */
  private fromNumber(index: number): Deduction | null {
    const list = this.lists[index] as Placement[];
    const first = list[0] as Placement;
    if (list.length === 1) return this.fromBlock(1, index, first.cells, first.border);
    let inEvery = first.cells;
    let borderOfEvery = first.border;
    let inAny = first.cells;
    for (let position = 1; position < list.length; position += 1) {
      const placement = list[position] as Placement;
      inEvery = maskIntersect(inEvery, placement.cells);
      borderOfEvery = maskIntersect(borderOfEvery, placement.border);
      inAny = maskUnion(inAny, placement.cells);
    }
    return this.fromBlock(2, index, inEvery, maskWithout(borderOfEvery, inAny));
  }

  /** A deduction from a block of cells and a border, or null if it says nothing new. */
  private fromBlock(rung: Rung, index: number, cells: Mask, border: Mask): Deduction | null {
    const shade = (this.clues[index] as Clue).shade;
    const assignments: Assignment[] = [];
    for (const cell of maskCells(cells)) {
      if (this.shades[cell] === UNKNOWN) assignments.push({ cell, shade });
    }
    for (const cell of maskCells(border)) {
      if (this.shades[cell] === UNKNOWN) assignments.push({ cell, shade: other(shade) });
    }
    if (assignments.length === 0) return null;
    return { rung, clue: index, cell: null, assignments };
  }
}

// ── Grading ─────────────────────────────────────────────────────────────────

export interface Analysis {
  /** Whether the ladder finished the board at all. */
  solved: boolean;
  hardest: Rung;
  /** How many times it had to fall back to a trial. */
  trials: number;
  steps: number;
  /** Steps taken on each rung, indexed by rung. */
  perRung: number[];
}

export function analyse(
  puzzle: Puzzle,
  deadline = Number.POSITIVE_INFINITY,
  lists?: readonly Placement[][],
): Analysis {
  const deducer = new Deducer(puzzle, undefined, lists);
  let hardest: Rung = 1;
  let steps = 0;
  let trials = 0;
  const perRung = [0, 0, 0, 0, 0];

  for (;;) {
    if (deducer.solved() || deducer.broken || Date.now() > deadline) break;
    let step = deducer.next();
    if (!step) {
      if (deducer.broken) break;
      step = deducer.trial(deadline);
      if (!step) break;
      trials += 1;
    }
    if (step.rung > hardest) hardest = step.rung;
    perRung[step.rung] = (perRung[step.rung] as number) + 1;
    deducer.apply(step);
    steps += 1;
  }

  return { solved: deducer.solved() && !deducer.broken, hardest, trials, steps, perRung };
}

/**
 * Where the four tiers are cut.
 *
 * Trials first, because a trial is the move where you stop reading the board and
 * start supposing: a board that needs none is a board you can talk your way
 * through, and that is the line between the bottom two tiers and the top two.
 *
 * Below that line the measure is how many steps needed more than reading a
 * number off the board — rungs 2 and 3, where a number's shapes have to be
 * compared or a cell's possible owners counted. Above it, the measure is how
 * many trials, since one or two is a puzzle you grind out and a dozen is a
 * different activity.
 *
 * Both numbers are medians of what the generator actually produces at each board
 * size: on the symmetric boards the generator builds, the hard board's trial
 * count sits at four and the expert board's at thirteen, so a cut at six splits
 * them where they actually separate. Rerun
 * `__bench/calibrate.ts` after touching the ladder, `SIZE_WEIGHTS`, `MIN_REGION`
 * or the way blocks are grown; all of them move the distribution, and stale
 * cut-offs would file every board under one label.
 *
 * The top tier has a second thing going for it that no cut-off can measure —
 * some of its numbers are missing — and that lands on the same side of this
 * line, since a board is only allowed to lose a number it can still be reasoned
 * without, and every number lost is more trials.
 */
export const EASY_STEPS = 1;
export const TRIAL_CUT = 6;

/**
 * The tier a board earns, or null when the ladder cannot finish it at all.
 *
 * Null is a refusal rather than a top tier, and the generator drops those boards
 * on the floor. A board can be perfectly unique and still be one this ladder
 * never cracks — big regions and few numbers make openings scarce, and the ones
 * that stall do it early, twenty squares into sixty-four with nothing to go on.
 * Filing that under "expert" would have made the hardest tier mean "beat our
 * solver", and it would have shipped boards whose hint button has nothing to say
 * but *try a different corner*. The tier a board is given is what solving it
 * takes, so a board nothing takes is not a board.
 */
export function grade(puzzle: Puzzle, deadline = Number.POSITIVE_INFINITY): Difficulty | null {
  const analysis = analyse(puzzle, deadline);
  if (!analysis.solved) return null;
  if (analysis.trials === 0) {
    const worked = (analysis.perRung[2] as number) + (analysis.perRung[3] as number);
    return worked <= EASY_STEPS ? 'easy' : 'medium';
  }
  return analysis.trials <= TRIAL_CUT ? 'hard' : 'expert';
}

// ── Flow ────────────────────────────────────────────────────────────────────

export interface Flow {
  /**
   * How often the next step was right beside the last one, from 0 to 1.
   *
   * The thing being measured is whether a board *leads* the player. After each
   * step, is there something to see touching the squares it just settled — the
   * consequence of the move sitting next to the move — or does the player have
   * to leave and look somewhere else? A board that scores high reads like a
   * thread being pulled: every answer uncovers the next question.
   */
  follow: number;
  /**
   * How many things the board offers on the easiest rung before anything is
   * shaded.
   *
   * One or two is a way in. Ten is a board where the first minute is spent
   * filling in whatever is obvious all over the grid, with no thread to follow
   * because every square is its own opening.
   */
  openings: number;
  /** The two folded into one number, higher is better, for ranking boards. */
  score: number;
}

/**
 * Openings beyond this many start to cost a board its thread.
 *
 * Three: one each for a couple of corners and something in the middle is still
 * a board with a shape to it. Past that, every extra opening is a place the
 * player works in isolation from everything else.
 */
const OPENINGS_WELCOME = 3;

/**
 * How a board plays from one step to the next, or null when the ladder stalls.
 *
 * Solved the way a person would follow it: at each point, of everything on the
 * easiest rung, the step taken is the one nearest the squares the previous step
 * settled, and when nothing is left but a trial, the trial starts beside them
 * too. The rung counts that grading uses do not change with that choice — the
 * tier is the same whichever order a person goes in — but whether there is
 * always *something nearby* does, and that is what this reports.
 *
 * A trial that breaks right where the player was counts as following, which is
 * fair: supposing a shade for the square next to the one you just settled is
 * the natural next thought. One that has to start across the board does not.
 */
export function flowOf(puzzle: Puzzle, deadline = Number.POSITIVE_INFINITY): Flow | null {
  const deducer = new Deducer(puzzle);
  const width = puzzle.width;
  const distance = (a: number, b: number): number =>
    Math.abs(Math.floor(a / width) - Math.floor(b / width)) + Math.abs((a % width) - (b % width));
  const nearest = (cells: readonly number[], to: readonly number[]): number => {
    let best = Number.POSITIVE_INFINITY;
    for (const cell of cells) for (const other of to) best = Math.min(best, distance(cell, other));
    return best;
  };

  let last: number[] = [];
  let openings = -1;
  let steps = 0;
  let followed = 0;
  for (;;) {
    if (deducer.solved() || deducer.broken || Date.now() > deadline) break;
    const options = deducer.options();
    if (openings < 0) openings = options.length;
    let step: Deduction | null = null;
    if (options.length > 0) {
      let bestGap = Number.POSITIVE_INFINITY;
      for (const option of options) {
        const gap = nearest(
          option.assignments.map((assignment) => assignment.cell),
          last,
        );
        if (gap < bestGap) {
          bestGap = gap;
          step = option;
        }
      }
      step ??= options[0] as Deduction;
    } else {
      if (deducer.broken) break;
      step = deducer.trial(deadline, last);
      if (!step) break;
    }
    const settled = step.assignments.map((assignment) => assignment.cell);
    if (last.length > 0) {
      steps += 1;
      const anchor = step.cell !== null && step.rung === 4 ? [step.cell] : settled;
      if (nearest(anchor, last) <= 1) followed += 1;
    }
    deducer.apply(step);
    last = settled;
  }
  if (!deducer.solved() || deducer.broken) return null;

  const follow = steps === 0 ? 1 : followed / steps;
  const flood = Math.max(0, openings - OPENINGS_WELCOME);
  return { follow, openings, score: follow - 0.04 * flood };
}
